import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MailWarning, RefreshCw, UserCog } from "lucide-react";
import api from "../../lib/api";
import { reportError } from "../../lib/errorReporter";
import { deliveryErrorKey, platformErrorMessage } from "../../lib/platformValidation";
import { fmtDateTime } from "../../lib/format";
import PlatformOwnerReplaceDialog from "./PlatformOwnerReplaceDialog";

const DELIVERY_LABELS = {
  unsent: "platform.delivery_unsent",
  sending: "platform.delivery_sending",
  delivered_to_provider: "platform.delivery_sent",
  delivery_failed: "platform.delivery_failed",
};

const Row = ({ label, value, testId }) => (
  <div className="qw-life-row">
    <span className="qw-life-label">{label}</span>
    <span data-testid={testId}>{value || "—"}</span>
  </div>
);

/**
 * Who owns this organization — or who has been invited to.
 *
 * A ``pending_activation`` organization has NO owner membership and exactly one
 * pending invitation; an ``active`` one has exactly one active owner and no
 * pending invitation. Both actions here are offered only when the backend says
 * they are legal right now (``lifecycle.can_edit_owner_invitation``), so the
 * console can never present an action the database would refuse.
 *
 * E-mail delivery is NOT part of the database transaction. A failed delivery is
 * therefore a visible state with a working resend, never an error page: the
 * organization stays awaiting activation and the invitation stays pending.
 */
export const PlatformOwnerCard = ({ snapshot, organizationId, onSnapshot }) => {
  const { t } = useTranslation();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const inFlight = useRef(false);

  const owner = snapshot?.owner || null;
  const invitation = snapshot?.owner_invitation || null;
  const lifecycle = snapshot?.lifecycle || {};
  const canEdit = Boolean(lifecycle.can_edit_owner_invitation);

  const applyResult = (data, successKey) => {
    onSnapshot(data);
    const delivery = data?.delivery;
    if (delivery && delivery.delivered === false) {
      setNotice("");
      setError(
        `${t("platform.delivery_not_sent")} ${t(deliveryErrorKey(delivery.error_code))}`,
      );
      return;
    }
    setError("");
    setNotice(t(successKey));
  };

  const resend = async () => {
    if (inFlight.current || busy) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const r = await api.post(
        `/platform-admin/organizations/${organizationId}/owner-invitation/resend`,
        {},
        { skipOrgHeader: true },
      );
      applyResult(r.data, "platform.resent");
    } catch (err) {
      reportError("platform.resendOwnerInvitation", err);
      setError(platformErrorMessage(err, t("common.error_generic")));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="qw-card" data-testid="platform-owner-card">
      <div style={{ fontWeight: 600, marginBottom: 12 }}>{t("platform.owner_card")}</div>

      {owner ? (
        <div className="qw-life-grid" data-testid="platform-owner-active">
          <Row
            label={t("platform.owner_email_label")}
            value={owner.email}
            testId="platform-owner-email"
          />
          <Row
            label={t("platform.owner_name_label")}
            value={owner.full_name}
            testId="platform-owner-name"
          />
          <Row
            label={t("platform.owner_member_since")}
            value={fmtDateTime(owner.member_since)}
            testId="platform-owner-since"
          />
        </div>
      ) : invitation ? (
        <div className="qw-life-grid" data-testid="platform-owner-invitation">
          <Row
            label={t("platform.invitation_email")}
            value={invitation.invited_email}
            testId="platform-invitation-email"
          />
          <Row
            label={t("platform.owner_name_label")}
            value={`${invitation.first_name || ""} ${invitation.last_name || ""}`.trim()}
            testId="platform-invitation-name"
          />
          <Row
            label={t("platform.invitation_phone")}
            value={invitation.phone}
            testId="platform-invitation-phone"
          />
          <Row
            label={t("platform.invitation_flow")}
            value={invitation.flow ? t(`platform.flow_${invitation.flow}`) : "—"}
            testId="platform-invitation-flow"
          />
          <Row
            label={t("platform.delivery_label")}
            value={
              invitation.delivery_status
                ? t(DELIVERY_LABELS[invitation.delivery_status] || "platform.delivery_unsent")
                : "—"
            }
            testId="platform-invitation-delivery"
          />
          <Row
            label={t("platform.invitation_expires")}
            value={
              invitation.expired
                ? `${fmtDateTime(invitation.expires_at)} · ${t("platform.invitation_expired")}`
                : fmtDateTime(invitation.expires_at)
            }
            testId="platform-invitation-expires"
          />
          <Row
            label={t("platform.invitation_resend_count")}
            value={String(invitation.resend_count ?? 0)}
            testId="platform-invitation-resend-count"
          />
        </div>
      ) : (
        <div className="qw-empty" data-testid="platform-owner-none">
          {t("platform.invitation_none")}
        </div>
      )}

      {invitation?.last_delivery_error_code && (
        <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-invitation-delivery-error">
          <MailWarning size={13} strokeWidth={2} aria-hidden="true" />{" "}
          {t(deliveryErrorKey(invitation.last_delivery_error_code))}
        </div>
      )}

      {error && (
        <div className="qw-error" style={{ marginTop: 10 }} data-testid="platform-owner-error">
          {error}
        </div>
      )}
      {notice && (
        <div
          className="qw-help"
          style={{ color: "#059669", marginTop: 10 }}
          data-testid="platform-owner-notice"
        >
          {notice}
        </div>
      )}

      {canEdit && (
        <div className="hstack" style={{ marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            className="qw-btn primary sm"
            onClick={resend}
            disabled={busy || !invitation}
            data-testid="platform-invitation-resend"
          >
            <RefreshCw size={14} strokeWidth={2} aria-hidden="true" />
            {busy ? t("platform.sending") : t("platform.resend_invitation")}
          </button>
          <button
            type="button"
            className="qw-btn ghost sm"
            onClick={() => {
              setError("");
              setNotice("");
              setReplacing(true);
            }}
            disabled={busy || !invitation}
            data-testid="platform-invitation-replace-open"
          >
            <UserCog size={14} strokeWidth={2} aria-hidden="true" />
            {t("platform.replace_owner")}
          </button>
        </div>
      )}

      <PlatformOwnerReplaceDialog
        open={replacing}
        organizationId={organizationId}
        invitation={invitation}
        onCancel={() => setReplacing(false)}
        onReplaced={(data) => {
          setReplacing(false);
          applyResult(data, "platform.replaced");
        }}
      />
    </div>
  );
};

export default PlatformOwnerCard;
